import React, { useState } from 'react';
import { Play, X, ChevronDown, ChevronUp, Video } from 'lucide-react';

/**
 * Protected YouTube Embed Component (Collapsible)
 * 
 * Features:
 * - Collapsible video section
 * - Center aligned video player
 * - Uses youtube-nocookie.com for privacy
 * - Disables related videos (rel=0)
 * - Reduces YouTube branding (modestbranding=1)
 * - Disables fullscreen to prevent escape (fs=0)
 * - Hides video annotations (iv_load_policy=3)
 * - Transparent overlay to prevent clicking YouTube elements
 * - Custom play button for better UX
 */

const YouTubeEmbed = ({ videoUrl, title, stepNumber }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);

  // Extract video ID from various YouTube URL formats
  const getYouTubeVideoId = (url) => {
    if (!url) return null;
    
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /^([a-zA-Z0-9_-]{11})$/ // Direct video ID
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const videoId = getYouTubeVideoId(videoUrl);

  if (!videoId) {
    return null;
  }

  // Build embed URL with privacy and branding parameters
  const embedParams = new URLSearchParams({
    rel: '0',           // Don't show related videos
    modestbranding: '1', // Reduce YouTube branding
    fs: '0',            // Disable fullscreen button
    iv_load_policy: '3', // Hide video annotations
    disablekb: '1',     // Disable keyboard controls
    playsinline: '1',   // Play inline on mobile
    enablejsapi: '1',   // Enable JS API for better control
    origin: window.location.origin,
  });

  // Use youtube-nocookie.com for enhanced privacy
  const embedUrl = `https://www.youtube-nocookie.com/embed/${videoId}?${embedParams.toString()}`;

  const handlePlay = () => {
    setIsPlaying(true);
    setShowVideo(true);
  };

  const handleClose = () => {
    setIsPlaying(false);
    setShowVideo(false);
  };

  const toggleExpanded = () => {
    setIsExpanded(!isExpanded);
    if (!isExpanded) {
      // Reset video state when collapsing
      setIsPlaying(false);
      setShowVideo(false);
    }
  };

  return (
    <div className="mb-2">
      {/* Collapsible Header */}
      <button
        onClick={toggleExpanded}
        className="w-full flex items-center justify-between py-2 px-3 rounded-lg bg-slate-800/30 hover:bg-slate-800/50 transition-colors border border-slate-700/30"
        data-testid="video-guide-toggle"
      >
        <div className="flex items-center gap-2">
          <div className="w-6 h-6 rounded-full bg-red-500/20 flex items-center justify-center">
            <Video size={12} className="text-red-400" />
          </div>
          <span className="text-xs font-medium text-slate-300">
            {title || `Step ${stepNumber} Video Guide`}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500">
            {isExpanded ? 'Hide' : 'Watch'}
          </span>
          {isExpanded ? (
            <ChevronUp size={14} className="text-slate-400" />
          ) : (
            <ChevronDown size={14} className="text-slate-400" />
          )}
        </div>
      </button>

      {/* Collapsible Video Content */}
      {isExpanded && (
        <div className="mt-3 flex flex-col items-center">
          {/* Video Container - Center aligned */}
          <div 
            className="relative rounded-lg overflow-hidden bg-slate-800/50 border border-slate-700/50 w-full max-w-md"
            style={{ aspectRatio: '16/9' }}
          >
            {!showVideo ? (
              // Thumbnail/Play Button State with YouTube Thumbnail
              <button
                onClick={handlePlay}
                className="absolute inset-0 flex flex-col items-center justify-center gap-2 group overflow-hidden"
              >
                {/* YouTube Thumbnail Background - using maxresdefault for hi-res (1280x720) */}
                <img 
                  src={`https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`}
                  alt={title || 'Video thumbnail'}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  onError={(e) => {
                    // Fallback chain: maxresdefault → sddefault → hqdefault → mqdefault
                    const fallbacks = [
                      `https://img.youtube.com/vi/${videoId}/sddefault.jpg`,
                      `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`,
                      `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`
                    ];
                    const currentSrc = e.target.src;
                    const currentIndex = fallbacks.findIndex(url => currentSrc.includes(url.split('/').pop().split('.')[0]));
                    if (currentIndex < fallbacks.length - 1) {
                      e.target.src = fallbacks[currentIndex + 1] || fallbacks[0];
                    }
                  }}
                />
                {/* Dark overlay for better contrast */}
                <div className="absolute inset-0 bg-black/40 group-hover:bg-black/30 transition-colors duration-300" />
                
                {/* Play button */}
                <div className="relative z-10 w-12 h-12 rounded-full bg-red-600 flex items-center justify-center shadow-lg shadow-red-600/40 group-hover:scale-110 group-hover:bg-red-500 transition-all duration-300">
                  <Play size={20} className="text-white ml-0.5" fill="white" />
                </div>
                <span className="relative z-10 text-white text-xs font-medium px-2 text-center drop-shadow-lg">
                  Click to play video
                </span>
              </button>
            ) : (
              // Video Playing State
              <div className="relative w-full h-full">
                {/* Close button */}
                <button
                  onClick={handleClose}
                  className="absolute top-2 right-2 z-10 text-white/70 hover:text-white bg-black/50 hover:bg-black/70 transition-colors p-1.5 rounded-full"
                  title="Close video"
                >
                  <X size={14} />
                </button>
                
                {/* YouTube iFrame */}
                <iframe
                  src={isPlaying ? `${embedUrl}&autoplay=1` : embedUrl}
                  title={title || `Step ${stepNumber} Video Guide`}
                  className="absolute inset-0 w-full h-full"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  referrerPolicy="strict-origin-when-cross-origin"
                  loading="lazy"
                />
                
                {/* Transparent Overlay - Covers corners to hide YouTube logo */}
                <div 
                  className="absolute bottom-0 right-0 w-32 h-12 bg-transparent cursor-default"
                  style={{ pointerEvents: 'auto' }}
                  onClick={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                />
                <div 
                  className="absolute top-0 left-0 w-24 h-10 bg-transparent cursor-default"
                  style={{ pointerEvents: 'auto' }}
                  onClick={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                />
                <div 
                  className="absolute top-0 right-0 w-24 h-10 bg-transparent cursor-default"
                  style={{ pointerEvents: 'auto' }}
                  onClick={(e) => e.preventDefault()}
                  onContextMenu={(e) => e.preventDefault()}
                />
              </div>
            )}
          </div>
        </div>
      )}

      {/* Disable right-click on the entire container */}
      <style>{`
        .youtube-embed-container iframe {
          pointer-events: auto;
        }
        .youtube-embed-container {
          -webkit-touch-callout: none;
          -webkit-user-select: none;
          -khtml-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
      `}</style>
    </div>
  );
};

export default YouTubeEmbed;
